import request from "../base-request";

export interface EmployeeSearchProps {
	selectionObjectID?: string;
	searchTerm?: string;
	page?: number;
	/** if true, only return selected users */
	limitSelected?: boolean;
	limitToDirectReports?: boolean;
	/** list of user selection IDs to exclude */
	exclusionList?: string[];
	usersToExcludeSelectionId?: string;
	excludeCurrentUser?: boolean;
	filters?: { [key: string]: string | Array<string> };
	additionalKeys?: string[];
}

interface EmployeesResponse {
	custom_fields: { _id: string; name: string; type: string }[];
	departments: { _id: string; name: string; numUsers: number }[];
	page_count: number;
	display_user_map: Record<string, { name: string; active: boolean }>;
	selected_users: Record<string, boolean>;
	users: {
		_id: string;
		name: string;
		data_fields: Record<string, { field_name: string; value: any }>;
		admin_fields: {
			admin: boolean;
			basic_admin: boolean;
			billing_admin: boolean;
			deactivated: boolean;
			invited: boolean;
			last_seen: string;
		};
	}[];
}

export type EmployeesResponseData = {
	users: {
		data_fields: Record<
			string,
			{
				_id: string;
				label: string;
				fieldType: string;
				value: any;
			}
		>;
		user_is_selected: boolean;
		_id: string;
		name: string;
		admin_fields: {
			admin: boolean;
			basic_admin: boolean;
			billing_admin: boolean;
			deactivated: boolean;
			invited: boolean;
			last_seen: string;
		};
	}[];
	pages: number;
};

/**
 * This takes the response from the API and simplifies it so it is easier to
 * work with in the UI.
 * @param props
 * @returns
 */
export const fetchEmployees = async (props: EmployeeSearchProps): Promise<EmployeesResponseData> => {
	const { selectionObjectID } = props;

	const queryParams = new URLSearchParams();

	if (props.searchTerm) {
		queryParams.set("q", props.searchTerm);
	}

	if (props.excludeCurrentUser) {
		queryParams.set("excludeCurrentUser", "true");
	}

	if (props.filters) {
		Object.keys(props.filters).forEach((filter) => {
			if (props.filters?.[filter]) {
				let queryParam = "";
				let queryFilterKey = "";
				if (props.filters[filter] instanceof Array) {
					queryParam = (props.filters[filter] as Array<string>).join(",");
					queryFilterKey = `${filter}[]`;
				} else {
					queryParam = props.filters[filter] as string;
					queryFilterKey = filter;
				}
				queryParams.set(queryFilterKey, queryParam);
			}
		});
	}

	if (props.page) {
		queryParams.set("page", props.page.toString());
	}

	if (props.limitSelected) {
		queryParams.set("limit_selected", "true");
	}

	if (props.limitToDirectReports) {
		queryParams.set("limit_to_direct_reports", "true");
	}

	if (props.exclusionList) {
		queryParams.set("exclusionList[]", props.exclusionList.join(","));
	}

	if (props.usersToExcludeSelectionId) {
		queryParams.set("usersToExcludeSelectionId", props.usersToExcludeSelectionId);
	}

	const baseUrl = props.selectionObjectID ? `/api/user/${selectionObjectID}` : "/api/user";
	const url = `${baseUrl}?${queryParams.toString()}`;
	const [data] = await request<EmployeesResponse>(url);

	const indexGroupsByID = data.departments.reduce<Record<string, { _id: string; name: string; numUsers: number }>>(
		(acc, group) => {
			acc[group._id] = group;
			return acc;
		},
		{}
	);

	// Iterate through each user and in each user, iterate through each field
	// to create an array of fields for each user.
	const users = data.users.map((user) => {
		const dataFields = data.custom_fields.map((field) => {
			let fieldValue = user.data_fields[field._id]?.value ?? "";

			switch (field.type) {
				case "department": {
					if (indexGroupsByID[fieldValue]) {
						fieldValue = indexGroupsByID[fieldValue];
					} else {
						fieldValue = null;
					}
					break;
				}

				case "date": {
					fieldValue = new Date(fieldValue);
					break;
				}

				case "person": {
					fieldValue = {
						_id: fieldValue,
						name: data.display_user_map[fieldValue]?.name ?? "",
					};
					break;
				}
			}

			return {
				_id: field._id,
				label: field.name,
				fieldType: field.type,
				value: fieldValue,
			};
		});

		// index fields by label
		const indexFieldsByLabel = dataFields.reduce<
			Record<string, { _id: string; label: string; fieldType: string; value: any }>
		>((acc, field) => {
			acc[field.label] = field;
			return acc;
		}, {});

		const userIsSelected = data.selected_users[user._id] ?? false;

		return {
			...user,
			data_fields: indexFieldsByLabel,
			user_is_selected: userIsSelected,
		};
	});

	return {
		users,
		pages: data.page_count,
	} as EmployeesResponseData;
};

export interface IEmployee {
	actve: boolean;
	company: {
		id: string;
		name: string;
	};
	directReports: string[];
	email: string;
	id: string;
	name: string;
	jobTitle: string;
}

export const getEmployeeByID = async (employeeID: string) => {
	const [employee] = await request<IEmployee>(`/api/v1/users/${employeeID}`);

	return employee;
};

export const deleteEmployeeAvatar = async (employeeID: string) => {
	await request(`/api/v0.1/users/${employeeID}/avatar/delete/`, {
		method: "POST",
	});
};
